import { subject } from '@casl/ability';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Alert, AlertList } from '@oncarrot/turnip';
import type { UseQueryResult } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { Can } from 'context/AbilityProvider/Can';
import { useAbilities } from 'context/AbilityProvider/useAbilities';
import {
  type AccountsQuery,
  OnboardingStatus,
  type SitesVisitorsQuery,
  type SitesQuery,
} from 'generated/gql/account-manager/graphql';
import { useAmQuery } from 'graphql/useAmQuery';
import { useGuaranteedCurrentUser } from 'hooks/useCurrentUser';
import { CenteredPageLoadingCarrot } from 'turnip/LoadingCarrot/LoadingCarrot';
import { Offset } from 'turnip/Offset/Offset';
import { PageLayoutContained } from 'turnip/PageLayoutContained/PageLayoutContained';
import { PageTitle } from 'turnip/PageTitle/PageTitle';
import { PowerTable } from 'turnip/PowerTable/PowerTable';
import { Stack } from 'turnip/Stack/Stack';
import { StatBadge } from 'turnip/StatBadge/StatBadge';
import { StyledLink } from 'turnip/StyledLink/StyledLink';
import { SupportLink } from 'turnip/SupportLink/SupportLink';
import type { Maybe } from 'types/Utilities';
import { carrotDomain } from 'utils/carrotDomain';
import { accountBasedRoutes } from 'utils/accountBasedRoutes';
import { wpAdminUrl } from 'utils/carrotUrls';
import {
  accountsQueryDocument,
  sitesVisitorsQueryDocument,
  sitesQueryDocument,
} from './Home.graphql.am';

export function Home() {
  const user = useGuaranteedCurrentUser();

  if (user.superAdmin) {
    return (
      <Layout>
        <Alert variant="info">This page cannot be used by superadmins.</Alert>
      </Layout>
    );
  }

  return <PageBody />;
}

function PageBody() {
  const blogsQuery = useAmQuery(sitesQueryDocument, {});
  const visitorsQuery = useAmQuery(sitesVisitorsQueryDocument, { days: 7 });
  const accountsWithIncompleteOnboardingQuery =
    useAccountsWithIncompleteOnboarding();

  if (blogsQuery.isPending || accountsWithIncompleteOnboardingQuery.isPending) {
    return <CenteredPageLoadingCarrot title="Loading accounts and sites" />;
  }

  return (
    <Layout>
      <Stack gap={10}>
        <IncompleteOnboardingAlert
          accounts={accountsWithIncompleteOnboardingQuery.data}
          isError={accountsWithIncompleteOnboardingQuery.isError}
        />

        <SitesList blogsQuery={blogsQuery} visitorsQuery={visitorsQuery} />
      </Stack>
    </Layout>
  );
}

function SitesList({
  blogsQuery,
  visitorsQuery,
}: {
  blogsQuery: UseQueryResult<SitesQuery>;
  visitorsQuery: UseQueryResult<SitesVisitorsQuery>;
}) {
  if (blogsQuery.isError) {
    return (
      <Alert variant="error" headline="Something went wrong">
        Please try again or <SupportLink />.
      </Alert>
    );
  }

  if (!blogsQuery.data?.sites.length) {
    return (
      <Alert variant="info" headline="No sites found">
        You don&apos;t have any sites yet.
      </Alert>
    );
  }

  const mergedData = blogsQuery.data?.sites.map((blog) => {
    const stats =
      visitorsQuery.data?.siteDashboardVisitor.visitorTotalPerSite.find(
        (v) =>
          // visitors subdomain is just the first part, e.g., testuserseller
          v.subdomain ===
          // blog subdomain is the full domain, e.g., testuserseller.carrot.pizza,
          // so we need to strip the Carrot domain
          blog.subdomain?.replace(`.${carrotDomain()}`, '')
      );

    return {
      ...blog,
      visitors: stats?.visitors,
      previousVisitors: stats?.previousVisitors,
    };
  });

  return (
    <PowerTable
      caption="Sites"
      columns={createTableColumns(visitorsQuery.isPending)}
      data={mergedData}
      roundedBorder
      showSearch
      pagination
      initialPageSize={20}
      initialSorting={[{ id: 'name', desc: false }]}
    />
  );
}

function Layout({ children }: { children: React.ReactNode }) {
  return (
    <PageLayoutContained>
      <PageTitle>Home</PageTitle>
      <Offset top={6}>{children}</Offset>
    </PageLayoutContained>
  );
}

type SiteRowWithStats = SitesQuery['sites'][number] & {
  visitors: Maybe<number>;
  previousVisitors: Maybe<number>;
};

function createTableColumns(visitorsQueryIsPending: boolean) {
  const col = createColumnHelper<SiteRowWithStats>();

  return [
    col.accessor((row) => `${row.name} ${row.mappedDomain ?? row.subdomain}`, {
      id: 'name',
      header: 'Site',
      cell: (context) => {
        const blogName = context.row.original.name;
        const { accountId, platformSiteId, mappedDomain, subdomain } =
          context.row.original;
        const siteSubject = subject('Site', {
          platform_site_id: platformSiteId,
        });

        return (
          <div className="flex flex-col items-start">
            <Can I="update" this={siteSubject}>
              <StyledLink
                to={accountBasedRoutes(accountId, platformSiteId).site}
                variant="strong"
              >
                {blogName}
              </StyledLink>
            </Can>
            <Can not I="update" this={siteSubject}>
              <span className="font-semibold">{blogName}</span>
            </Can>
            <StyledLink
              to={`https://${mappedDomain ?? subdomain}`}
              variant="plain"
            >
              {mappedDomain ?? subdomain}
            </StyledLink>
          </div>
        );
      },
    }),
    col.accessor('targetLeadType', {
      header: 'Lead Type',
    }),
    col.accessor('accountName', {
      header: 'Account',
      cell: ({ getValue, row }) => (
        <StyledLink
          to={accountBasedRoutes(row.original.accountId).account}
          variant="plain"
        >
          {getValue()}
        </StyledLink>
      ),
    }),
    col.accessor('visitors', {
      enableGlobalFilter: false,
      header: () => (
        <>
          Visitors{' '}
          {visitorsQueryIsPending && (
            <FontAwesomeIcon
              aria-label="Loading"
              aria-hidden={false}
              icon={faSpinner}
              spin
            />
          )}
        </>
      ),
      cell: ({ getValue, row }) => (
        <VisitorStats
          visitors={getValue()}
          previousVisitors={row.original.previousVisitors}
        />
      ),
    }),
    col.display({
      id: 'links',
      header: 'Links',
      cell: ({ row }) => {
        const blog = row.original;
        return (
          <div className="flex flex-wrap gap-x-2">
            <Can
              I="update"
              this={subject('Site', {
                platform_site_id: blog.platformSiteId,
              })}
            >
              <StyledLink
                to={
                  accountBasedRoutes(blog.accountId, blog.platformSiteId).site
                }
                variant="plain"
              >
                Dashboard
              </StyledLink>
            </Can>
            <ContentLink subdomain={blog.subdomain} />
          </div>
        );
      },
    }),
  ];
}

function ContentLink({ subdomain }: { subdomain: Maybe<string> }) {
  if (!subdomain) return null;

  return (
    <StyledLink
      to={wpAdminUrl(subdomain, 'edit.php?post_type=page')}
      variant="plain"
    >
      Content
    </StyledLink>
  );
}

function VisitorStats({
  visitors,
  previousVisitors,
}: {
  visitors?: Maybe<number>;
  previousVisitors?: Maybe<number>;
}) {
  if (!visitors) {
    return null;
  }

  const change = previousVisitors
    ? (visitors - previousVisitors) / previousVisitors
    : undefined;

  return (
    <div className="flex gap-1.5">
      <span>{visitors.toLocaleString()}</span>
      {typeof change === 'number' && <StatBadge value={change} size="small" />}
    </div>
  );
}

function IncompleteOnboardingAlert({
  accounts,
  isError,
}: {
  accounts: Array<AccountsQuery['accounts'][number]> | undefined;
  isError: boolean;
}) {
  if (isError) {
    return (
      <Alert variant="error" headline="Unable to load account data">
        Please try again or <SupportLink />.
      </Alert>
    );
  }

  if (!accounts?.length) {
    return null;
  }

  return (
    <Alert
      variant="warning"
      headline="You must complete onboarding for the following accounts"
    >
      <p>
        Onboarding will help you get up and running with a unique marketing
        strategy and website.
      </p>

      <Offset top={2}>
        <AlertList>
          {accounts.map(({ id, name }) => (
            <li key={id}>
              <StyledLink
                variant="contextual"
                to={accountBasedRoutes(id).onboarding.resume}
              >
                {name}
              </StyledLink>
            </li>
          ))}
        </AlertList>
      </Offset>
    </Alert>
  );
}

function useAccountsWithIncompleteOnboarding() {
  const ability = useAbilities();

  return useAmQuery(
    accountsQueryDocument,
    {},
    {
      select: (data) =>
        data.accounts
          .filter(
            (account) => account.onboardingStatus !== OnboardingStatus.Completed
          )
          .filter((account) =>
            ability.can('update', subject('Account', { id: account.id }))
          )
          .map((account) => ({
            id: account.id,
            name: account.name,
          })),
    }
  );
}
